import {
  COMPARE_SERIES_COLOR,
  TrendCurve,
  type TrendCurveSpec,
  type TrendLine,
} from '@/components/charts/TrendCurve';
/**
 * AuditMetricCharts — the per-page audit-history view on the Performance
 * Audits tab. Replaces the per-page tile breakdown with a vertical list
 * of full-width line charts, one chart per metric, each plotting that
 * metric across the page's audit history over time.
 *
 * Built on the shared `TrendCurve` (from `PerformanceSection`): a
 * smoothed line + deploy markers + per-point hover, with optional
 * threshold band lines. Web-Vitals metrics carry CWV thresholds (good /
 * poor dashed guides + band-coloured line); the page-weight + third-party
 * metrics are neutral (no thresholds → plain line).
 *
 * The filter row lives in the caller (`LabPerformance`), which owns the
 * Origin-vs-page scope, so Device / Region arrive as props and are
 * applied client-side to the audit pool here. Deploy markers come from
 * `useSiteDeploys` and are passed to every chart. Clicking a point jumps
 * to that single audit's report.
 */
import { FilterBar } from '@/components/primitives/FilterBar';
import { FilterSelect } from '@/components/primitives/FilterSelect';
import { Flag } from '@/components/primitives/Flag';
import { type PageRange, PageRangeSelect } from '@/components/primitives/PageRangePill';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectPrimitive,
  SelectValue,
} from '@/components/ui/select';
import type { AuditResponse, PageWithRecentAudits } from '@/lib/api-types';
import {
  type MetricBand,
  bandText,
  clsBand,
  fcpBand,
  formatBytes,
  formatMs,
  formatScore,
  inpBand,
  lcpBand,
  scoreBand,
  ttfbBand,
} from '@/lib/format';
import { useAudits } from '@/lib/queries/audits';
import { useSiteDeploys } from '@/lib/queries/deploys';
import { cn } from '@/lib/utils';
import type { Icon } from '@phosphor-icons/react';
import {
  CaretDown,
  Desktop,
  DeviceMobile,
  File,
  GlobeHemisphereWest,
  MapPin,
} from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, useMemo } from 'react';

// ─── Metric specs ────────────────────────────────────────────────────
//
// Three groups. Field accessors + formatters + bands mirror
// `PageAuditBreakdown` exactly. Web Vitals carry thresholds (CWV bands);
// page-weight + third-party metrics are neutral — no thresholds, a
// flat-`'unknown'` band paints a neutral line.

const neutralBand = (): MetricBand => 'unknown';

interface AuditMetricSpec extends TrendCurveSpec {
  /** Stable key for the line + React key. */
  metricKey: string;
  /** Pull this metric's value off an audit row. */
  accessor: (a: AuditResponse) => number | null;
}

const SCORES: readonly AuditMetricSpec[] = [
  {
    metricKey: 'performance_score',
    label: 'Performance',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
    accessor: (a) => a.scores?.performance ?? null,
  },
  {
    metricKey: 'accessibility_score',
    label: 'Accessibility',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
    accessor: (a) => a.scores?.accessibility ?? null,
  },
  {
    metricKey: 'best_practices_score',
    label: 'Best practices',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
    accessor: (a) => a.scores?.best_practices ?? null,
  },
  {
    metricKey: 'seo_score',
    label: 'SEO',
    unit: 'score',
    band: scoreBand,
    format: formatScore,
    goodThreshold: 90,
    poorThreshold: 50,
    accessor: (a) => a.scores?.seo ?? null,
  },
];

const WEB_VITALS: readonly AuditMetricSpec[] = [
  {
    metricKey: 'lcp',
    label: 'LCP',
    unit: 'ms',
    band: lcpBand,
    format: formatMs,
    goodThreshold: 2500,
    poorThreshold: 4000,
    accessor: (a) => a.metrics?.lcp_ms ?? null,
  },
  {
    metricKey: 'cls',
    label: 'CLS',
    unit: 'cls',
    band: clsBand,
    format: (v) => (v == null || !Number.isFinite(v) ? '—' : v.toFixed(2)),
    goodThreshold: 0.1,
    poorThreshold: 0.25,
    accessor: (a) => a.metrics?.cls ?? null,
  },
  {
    metricKey: 'inp',
    label: 'INP',
    unit: 'ms',
    band: inpBand,
    format: formatMs,
    goodThreshold: 200,
    poorThreshold: 500,
    accessor: (a) => a.metrics?.inp_ms ?? null,
  },
  {
    metricKey: 'ttfb',
    label: 'TTFB',
    unit: 'ms',
    band: ttfbBand,
    format: formatMs,
    goodThreshold: 800,
    poorThreshold: 1800,
    accessor: (a) => a.metrics?.ttfb_ms ?? null,
  },
  {
    metricKey: 'fcp',
    label: 'FCP',
    unit: 'ms',
    band: fcpBand,
    format: formatMs,
    goodThreshold: 1800,
    poorThreshold: 3000,
    accessor: (a) => a.metrics?.fcp_ms ?? null,
  },
];

const PAGE_WEIGHT: readonly AuditMetricSpec[] = [
  {
    metricKey: 'total_bytes',
    label: 'Total',
    unit: 'bytes',
    band: neutralBand,
    format: formatBytes,
    accessor: (a) => a.metrics?.total_bytes ?? null,
  },
  {
    metricKey: 'html_bytes',
    label: 'HTML',
    unit: 'bytes',
    band: neutralBand,
    format: formatBytes,
    accessor: (a) => a.metrics?.html_bytes ?? null,
  },
  {
    metricKey: 'js_bytes',
    label: 'JS',
    unit: 'bytes',
    band: neutralBand,
    format: formatBytes,
    accessor: (a) => a.metrics?.js_bytes ?? null,
  },
  {
    metricKey: 'css_bytes',
    label: 'CSS',
    unit: 'bytes',
    band: neutralBand,
    format: formatBytes,
    accessor: (a) => a.metrics?.css_bytes ?? null,
  },
  {
    metricKey: 'image_bytes',
    label: 'Images',
    unit: 'bytes',
    band: neutralBand,
    format: formatBytes,
    accessor: (a) => a.metrics?.image_bytes ?? null,
  },
  {
    metricKey: 'font_bytes',
    label: 'Fonts',
    unit: 'bytes',
    band: neutralBand,
    format: formatBytes,
    accessor: (a) => a.metrics?.font_bytes ?? null,
  },
];

const THIRD_PARTIES: readonly AuditMetricSpec[] = [
  {
    metricKey: 'third_party_bytes',
    label: 'Bytes',
    unit: 'bytes',
    band: neutralBand,
    format: formatBytes,
    accessor: (a) => a.metrics?.third_party_bytes ?? null,
  },
  {
    metricKey: 'third_party_blocking_ms',
    label: 'Blocking',
    unit: 'ms',
    band: neutralBand,
    format: formatMs,
    accessor: (a) => a.metrics?.third_party_blocking_ms ?? null,
  },
  {
    metricKey: 'third_party_entity_count',
    label: 'Entities',
    unit: 'count',
    band: neutralBand,
    format: (v) => (v == null ? '—' : String(v)),
    accessor: (a) => a.metrics?.third_party_entity_count ?? null,
  },
];

const GROUPS: ReadonlyArray<{ title: string; specs: readonly AuditMetricSpec[] }> = [
  { title: 'Scores', specs: SCORES },
  { title: 'Web Vitals', specs: WEB_VITALS },
  { title: 'Page weight', specs: PAGE_WEIGHT },
  { title: 'Third parties', specs: THIRD_PARTIES },
];

// ─── Filters ─────────────────────────────────────────────────────────

export type DeviceFilter = 'all' | 'mobile' | 'desktop';

// Labels stand alone as the dropdown's trigger text — hence "All
// devices" rather than the bare "All" the labelled segmented row used.
const DEVICE_OPTIONS: ReadonlyArray<{ value: DeviceFilter; label: string; icon: Icon }> = [
  { value: 'all', label: 'All devices', icon: GlobeHemisphereWest },
  { value: 'mobile', label: 'Mobile', icon: DeviceMobile },
  { value: 'desktop', label: 'Desktop', icon: Desktop },
];

/** Device picker — the same `FilterSelect` pill as its Region / date
 *  range / Compare neighbours, so the audit filter bar reads as one
 *  control family (matching the Real users bar). */
function DevicePicker({
  value,
  onChange,
}: {
  value: DeviceFilter;
  onChange: (next: DeviceFilter) => void;
}): ReactElement {
  const selected = DEVICE_OPTIONS.find((o) => o.value === value) ?? DEVICE_OPTIONS[0]!;
  const SelectedIcon = selected.icon;
  return (
    <FilterSelect
      ariaLabel="Device"
      icon={<SelectedIcon aria-hidden="true" size={12} className="text-[var(--color-mute)]" />}
      value={value}
      options={DEVICE_OPTIONS.map((opt) => {
        const OptionIcon = opt.icon;
        return {
          value: opt.value,
          label: opt.label,
          prefix: <OptionIcon aria-hidden="true" size={13} className="text-[var(--color-mute)]" />,
        };
      })}
      onChange={(next) => onChange(next as DeviceFilter)}
      active={value !== 'all'}
    />
  );
}

// GCP region → the country it physically lives in. The Region filter
// VALUE stays the raw `gcp_region` slug (it filters `a.region`); this
// map only drives the LABEL — an ISO-3166 alpha-2 code (for the `<Flag>`)
// + a human country name. Unmapped slugs fall back to the raw slug with
// no flag. Codes use the same `<Flag>` vocabulary as the Country picker.
const REGION_COUNTRY: Record<string, { code: string; name: string }> = {
  'asia-northeast1': { code: 'JP', name: 'Japan' },
  'asia-northeast2': { code: 'JP', name: 'Japan' },
  'asia-northeast3': { code: 'KR', name: 'South Korea' },
  'asia-southeast1': { code: 'SG', name: 'Singapore' },
  'asia-south1': { code: 'IN', name: 'India' },
  'australia-southeast1': { code: 'AU', name: 'Australia' },
  'europe-west1': { code: 'BE', name: 'Belgium' },
  'europe-west2': { code: 'GB', name: 'United Kingdom' },
  'europe-west3': { code: 'DE', name: 'Germany' },
  'europe-west4': { code: 'NL', name: 'Netherlands' },
  'europe-north1': { code: 'FI', name: 'Finland' },
  'us-central1': { code: 'US', name: 'United States' },
  'us-east1': { code: 'US', name: 'United States' },
  'us-east4': { code: 'US', name: 'United States' },
  'us-west1': { code: 'US', name: 'United States' },
  'southamerica-east1': { code: 'BR', name: 'Brazil' },
};

/** Region option label: flag + country name for mapped GCP regions, raw
 *  slug (no flag) for anything unmapped. Exported so the Origin filter
 *  bar labels its regions identically to the per-page one. */
export function regionLabel(region: string): ReactNode {
  const country = REGION_COUNTRY[region];
  if (!country) return region;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Flag code={country.code} className="h-3 w-[18px]" />
      {country.name}
    </span>
  );
}

/** Guest-page picker for compare mode. Keeps an eyebrow label ("vs
 *  {site}") because a second bare page pill next to the scope pill would
 *  be ambiguous about which side it belongs to. */
export function PageSelect({
  value,
  pages,
  onChange,
  label = 'Page',
}: {
  value: string;
  pages: PageWithRecentAudits[];
  onChange: (next: string) => void;
  /** Control label — "Page" for the home picker, "vs {site}" for the
   *  guest picker in compare mode. */
  label?: string;
}): ReactElement {
  const currentLabel = (() => {
    const p = pages.find((x) => x.uuid === value);
    return p ? pageLabel(p) : 'Page';
  })();
  return (
    <div className="flex items-center gap-1.5">
      <span className="max-w-40 truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </span>
      <Select
        value={value}
        onValueChange={(v) => {
          if (typeof v === 'string') onChange(v);
        }}
      >
        <SelectPrimitive.Trigger
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-2.5 text-xs font-semibold text-[var(--color-ink)] outline-none transition-colors',
            'hover:bg-[var(--color-paper-alt)]/60 data-[popup-open]:bg-[var(--color-paper-alt)]',
          )}
        >
          <SelectValue>
            {/* `block`, not `inline-block`: an inline-block with
                `overflow: hidden` aligns its bottom edge to the text
                baseline, which floated the label a pixel or two above
                its neighbours' centre line. */}
            <span className="block max-w-[180px] truncate">{currentLabel}</span>
          </SelectValue>
          <SelectPrimitive.Icon>
            <CaretDown aria-hidden="true" size={10} className="text-[var(--color-mute)]" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectContent>
          {pages.map((p) => (
            <SelectItem key={p.uuid} value={p.uuid}>
              {pageLabel(p)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Sentinel scope value for "the whole origin" — page uuids never
 *  collide with it. */
export const AUDIT_SCOPE_ORIGIN = 'origin';

/**
 * Scope picker — Origin (every audited page, averaged) or one page.
 *
 * This is what used to be the Origin / Pages sub-tab pair: the two views
 * answer the same question at different zoom levels, so the zoom belongs
 * in the filter row next to Device and Region rather than in a nav that
 * implies separate destinations.
 */
export function AuditScopeSelect({
  value,
  pages,
  onChange,
}: {
  /** Page uuid, or `AUDIT_SCOPE_ORIGIN`. */
  value: string;
  pages: PageWithRecentAudits[];
  onChange: (next: string) => void;
}): ReactElement {
  const scopedToPage = value !== AUDIT_SCOPE_ORIGIN;
  return (
    <FilterSelect
      ariaLabel="Audit scope"
      icon={
        scopedToPage ? (
          <File aria-hidden="true" size={12} className="text-[var(--color-mute)]" />
        ) : (
          <GlobeHemisphereWest aria-hidden="true" size={12} className="text-[var(--color-mute)]" />
        )
      }
      value={value}
      options={[
        { value: AUDIT_SCOPE_ORIGIN, label: 'Origin (all pages)' },
        ...pages.map((p) => ({
          value: p.uuid,
          label: pageLabel(p),
          prefix: <File aria-hidden="true" size={13} className="text-[var(--color-mute)]" />,
        })),
      ]}
      onChange={onChange}
      active={scopedToPage}
    />
  );
}

/** Region picker — a dropdown, not a segmented pill row. A pill per
 *  region overflows the filter bar once a site audits from more than a
 *  handful of locations; the dropdown stays a fixed h-7 control no
 *  matter how long the region list grows. */
function RegionSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: ReadonlyArray<{ value: string; label: ReactNode }>;
  onChange: (next: string) => void;
}): ReactElement {
  return (
    <FilterSelect
      ariaLabel="Region"
      icon={<MapPin aria-hidden="true" size={12} className="text-[var(--color-mute)]" />}
      value={value}
      options={options}
      onChange={onChange}
      active={value !== 'all'}
    />
  );
}

/** The Audits view's sticky filter row. The leading `children` slot
 *  carries the scope picker (Origin vs one page) plus, in compare mode,
 *  the guest-page picker; Device + Region + the date range + the route's
 *  Compare pill follow, so one bar drives whatever the scope resolves to.
 *
 *  Chrome (full-bleed, pinned, flush under the page header) comes from
 *  the shared `FilterBar`, which Real users and Field data use too. */
export function AuditFilterBar({
  device,
  onDevice,
  region,
  onRegion,
  regionOptions,
  range,
  onRange,
  actions,
  children,
}: {
  device: DeviceFilter;
  onDevice: (d: DeviceFilter) => void;
  region: string;
  onRegion: (r: string) => void;
  regionOptions: ReadonlyArray<{ value: string; label: ReactNode }>;
  range: PageRange;
  onRange: (r: PageRange) => void;
  /** Trailing slot beside the date range — the route drops its Compare
   *  picker here so the page has one control strip, not two. */
  actions?: ReactNode;
  children?: ReactNode;
}): ReactElement {
  return (
    <FilterBar>
      {children}
      <DevicePicker value={device} onChange={onDevice} />
      <RegionSelect value={region} options={regionOptions} onChange={onRegion} />
      {/* The window + Compare reframe the whole view rather than narrow
          its audit pool, so they anchor the right the way they do on
          Real users. */}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <PageRangeSelect value={range} onChange={onRange} />
        {actions}
      </div>
    </FilterBar>
  );
}

/** "14:32" — local hour:minute for the day-picker rows. Built once. */
const TIME_FORMATTER =
  typeof Intl !== 'undefined' && 'DateTimeFormat' in Intl
    ? new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit' })
    : null;

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(11, 16);
  return TIME_FORMATTER ? TIME_FORMATTER.format(d) : iso.slice(11, 16);
}

// ─── Per-metric chart ────────────────────────────────────────────────
//
// One full-width TrendCurve plus a small header line (metric label +
// latest non-null value). For CWV the value is band-tinted; neutral
// metrics show it in soft ink. Hidden by the parent when every point is
// null — sparse (<2 points) still renders; TrendCurve handles it.

function MetricChart({
  spec,
  pool,
  deploys,
  onPointClick,
  comparePool,
  homeKey,
  compareKey,
  pageNameById,
}: {
  spec: AuditMetricSpec;
  pool: AuditResponse[];
  deploys: ReturnType<typeof useSiteDeploys>['data'];
  /** Jump to a single audit's report when its point is clicked. */
  onPointClick: (id: string) => void;
  /** Guest page's audit pool in compare mode — overlays a second line.
   *  Undefined = single-site mode (unchanged behaviour). */
  comparePool?: AuditResponse[] | undefined;
  /** Legend/tooltip keys in compare mode — the two site names. */
  homeKey?: string | undefined;
  compareKey?: string | undefined;
  /** Page uuid → label, set only at origin scope, where one day's
   *  bucket spans several pages and the picker rows need naming. */
  pageNameById?: Map<string, string> | undefined;
}): ReactElement {
  // One averaged point per DAY, not one per audit. Plotting every raw
  // run drew a violent sawtooth: with DEVICE=All the pool interleaves
  // mobile (~36) and desktop (~90) audits by timestamp, and Lighthouse
  // scores bounce run-to-run regardless — so the daily mean is what
  // reads as a trend. Positioned at the day's real timestamp (noon) so
  // the time-axis still aligns the two sites by date and honours gaps.
  // Click-through survives only on single-audit days (a bucket of many
  // can't point at one report — "Open full report" covers that).
  const toPoints = (src: AuditResponse[]): TrendLine['points'] => {
    const byDay = new Map<string, AuditResponse[]>();
    for (const a of src) {
      if (spec.accessor(a) == null) continue;
      const day = a.created_at.slice(0, 10);
      const list = byDay.get(day) ?? [];
      list.push(a);
      byDay.set(day, list);
    }
    return [...byDay.entries()]
      .sort(([d1], [d2]) => d1.localeCompare(d2))
      .map(([day, list]) => {
        const vals = list.map((a) => spec.accessor(a) as number);
        const mean = vals.reduce((x, y) => x + y, 0) / vals.length;
        // The day's audits, newest first, as pickable rows — so a pip
        // that buckets many runs still lets the user open a specific
        // report (time · device, with this metric's value trailing).
        const items = [...list]
          .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
          .map((a) => {
            const page = a.ids?.page_uuid ? pageNameById?.get(a.ids.page_uuid) : undefined;
            const device = a.device === 'desktop' ? 'Desktop' : 'Mobile';
            return {
              id: a.id,
              label: `${formatTime(a.created_at)} · ${device}${page ? ` · ${page}` : ''}`,
              detail: spec.format(spec.accessor(a)),
            };
          });
        return {
          day,
          value: mean,
          t: Date.parse(`${day}T12:00:00Z`),
          items,
          ...(items.length === 1 ? { id: items[0]!.id } : {}),
        };
      });
  };
  const lines: TrendLine[] = comparePool
    ? [
        { key: homeKey ?? 'This site', color: 'var(--color-accent-brand)', points: toPoints(pool) },
        {
          key: compareKey ?? 'Other site',
          color: COMPARE_SERIES_COLOR,
          points: toPoints(comparePool),
          // Faint reference behind the home line — the home line keeps
          // its original band-coloured look.
          reference: true,
        },
      ]
    : [{ key: spec.metricKey, color: 'var(--color-accent-brand)', points: toPoints(pool) }];
  // Latest non-null HOME value, walking from newest (pool is ascending)
  // — the header value stays home-site in compare mode; the chart
  // carries the guest's numbers.
  let latest: number | null = null;
  for (let i = pool.length - 1; i >= 0; i -= 1) {
    const v = spec.accessor(pool[i]!);
    if (v != null) {
      latest = v;
      break;
    }
  }
  const neutral = spec.goodThreshold == null && spec.poorThreshold == null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 px-1">
        <span className="text-xs font-medium text-[var(--color-ink-soft)]">{spec.label}</span>
        <span
          className={cn(
            'text-xs font-semibold tabular-nums',
            neutral ? 'text-[var(--color-ink-soft)]' : bandText(spec.band(latest)),
          )}
        >
          {spec.format(latest)}
        </span>
      </div>
      <TrendCurve
        lines={lines}
        spec={spec}
        deploys={deploys ?? []}
        previousPoints={[]}
        onPointClick={onPointClick}
        timeAxis
        // Shorter than a card chart: these stack a dozen deep, so the
        // group stays scannable in one screenful.
        height={160}
      />
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────

/** Short label for a page in the Page segmented group: its `name`, or
 *  the URL's path (fallback to the raw url) when unnamed. */
function pageLabel(page: PageWithRecentAudits): string {
  if (page.name) return page.name;
  try {
    return new URL(page.url).pathname || page.url;
  } catch {
    return page.url;
  }
}

export interface AuditChartsCompare {
  /** Guest site uuid — drives the guest audit query. */
  siteUuid: string;
  /** Guest site display name — the guest line's legend/tooltip key. */
  siteName: string;
  /** Home site display name — the home line's key in compare mode. */
  homeName?: string | undefined;
  /** Guest site's audited pages — labels the guest line. */
  pages: PageWithRecentAudits[];
  /** Selected guest page, or null to compare origin-to-origin. */
  pageUuid: string | null;
}

export function AuditMetricCharts({
  siteUuid,
  pageUuid,
  pages,
  range,
  device,
  region,
  compare,
}: {
  siteUuid: string;
  /** Scope: one page's audit history, or null for the origin — every
   *  audited page, which `MetricChart`'s per-day mean then averages
   *  across pages the same way it already averages across runs. */
  pageUuid: string | null;
  /** Audited pages — labels the lines, and names the runs behind an
   *  origin-scoped point (a day there buckets several pages). */
  pages: PageWithRecentAudits[];
  /** Days of history to fetch. Drives the `created_after` window so the
   *  charts honour the selected range instead of just the newest handful
   *  of audits. */
  range: PageRange;
  /** Device / region narrowing, owned by the caller's filter bar (the
   *  same controls scope the origin summary), applied client-side to the
   *  audit pool here. */
  device: DeviceFilter;
  region: string;
  /** Site-compare mode: a guest page picked from the guest site,
   *  overlaid as a second line on every chart. Undefined = off. */
  compare?: AuditChartsCompare | undefined;
}): ReactElement {
  // Lower bound for the audit window. Memoised on `range` so a stable
  // timestamp per range selection keeps the query key (and cache) stable
  // across renders — recomputing `Date.now()` every render would thrash
  // the cache and refetch endlessly.
  const createdAfter = useMemo(
    () => new Date(Date.now() - range * 86_400_000).toISOString(),
    [range],
  );
  // 500 (the API cap) so a full range of a normally-audited page comes
  // back in one request. A very heavily-audited page over a long range
  // can exceed that — `has_more` surfaces the truncation to the caller.
  const audits = useAudits({
    limit: 500,
    offset: 0,
    include_ids: true,
    site_uuid: siteUuid,
    ...(pageUuid ? { page_uuid: pageUuid } : {}),
    created_after: createdAfter,
  });
  // Guest page's audits in compare mode. Hook runs unconditionally
  // (rules of hooks); the fetch is gated on compare being active.
  const guestAudits = useAudits(
    {
      limit: 500,
      offset: 0,
      include_ids: true,
      site_uuid: compare?.siteUuid ?? siteUuid,
      ...(compare?.pageUuid ? { page_uuid: compare.pageUuid } : {}),
      created_after: createdAfter,
    },
    { enabled: !!compare },
  );
  const deploys = useSiteDeploys(siteUuid, 120).data;
  const navigate = useNavigate();

  // Unfiltered pool — every audit for this page, ascending by time. The
  // region option list is derived from this so switching device doesn't
  // hide regions.
  const basePool = useMemo<AuditResponse[]>(() => {
    if (!audits.data) return [];
    return audits.data.audits
      .filter((a) => !pageUuid || a.ids?.page_uuid === pageUuid)
      .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  }, [audits.data, pageUuid]);

  const guestPageUuid = compare?.pageUuid;
  const guestBasePool = useMemo<AuditResponse[]>(() => {
    if (!compare || !guestAudits.data) return [];
    return guestAudits.data.audits
      .filter((a) => !guestPageUuid || a.ids?.page_uuid === guestPageUuid)
      .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  }, [compare, guestAudits.data, guestPageUuid]);

  // Origin-scoped days bucket runs from several pages, so the day
  // picker's rows need to say WHICH page each run was — "14:32 ·
  // Mobile" six times over is useless. Page scope already knows.
  const pageNameById = useMemo(() => {
    if (pageUuid) return undefined;
    return new Map(pages.map((p) => [p.uuid, pageLabel(p)]));
  }, [pageUuid, pages]);

  const pool = useMemo<AuditResponse[]>(
    () =>
      basePool.filter(
        (a) =>
          (device === 'all' || a.device === device) && (region === 'all' || a.region === region),
      ),
    [basePool, device, region],
  );

  // Guest pool honours the same Device/Region filters so both lines
  // describe the same lab audience.
  const guestPool = useMemo<AuditResponse[]>(
    () =>
      guestBasePool.filter(
        (a) =>
          (device === 'all' || a.device === device) && (region === 'all' || a.region === region),
      ),
    [guestBasePool, device, region],
  );

  const openAudit = (id: string): void => {
    void navigate({ to: '/performance/audits/$auditId', params: { auditId: id } });
  };

  // Legend/tooltip keys for the two lines. Cross-site compare labels by
  // SITE; same-site (page-vs-page) labels by PAGE — two identical site
  // names would be useless when the whole point is which page differs.
  const sameSite = !!compare && compare.siteUuid === siteUuid && !!pageUuid;
  const homePageForKey = pages.find((p) => p.uuid === pageUuid);
  const guestPageForKey = compare?.pages.find((p) => p.uuid === compare.pageUuid);
  const homeKey = !compare
    ? undefined
    : sameSite
      ? homePageForKey
        ? pageLabel(homePageForKey)
        : 'This page'
      : (compare.homeName ?? 'This site');
  const compareKey = !compare
    ? undefined
    : sameSite
      ? guestPageForKey
        ? pageLabel(guestPageForKey)
        : 'Other page'
      : compare.siteName;

  return (
    <div className="space-y-5">
      {audits.isLoading
        ? // Loading shell — render every group's chart frames as bordered
          // placeholders sized to match the populated charts. Keeps the page
          // SHAPE stable across the loading→data transition; without this
          // the bottom of the Audits view was just a sticky filter strip
          // hanging over empty space until the audits query landed.
          GROUPS.map((group) => (
            <section key={group.title} className="space-y-3">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
                {group.title}
              </h3>
              <div className="space-y-4">
                {group.specs.map((spec) => (
                  <MetricChartPlaceholder key={spec.metricKey} label={spec.label} />
                ))}
              </div>
            </section>
          ))
        : GROUPS.map((group) => {
            // A metric shows only when some audit in the (filtered) pool has
            // a non-null value for it — mirrors PageAuditBreakdown's `has`.
            // In compare mode the guest pool counts too, so a metric the
            // guest reports but home doesn't still gets a chart.
            const visible = group.specs.filter(
              (spec) =>
                pool.some((a) => spec.accessor(a) != null) ||
                guestPool.some((a) => spec.accessor(a) != null),
            );
            if (visible.length === 0) return null;
            return (
              <section key={group.title} className="space-y-3">
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  {group.title}
                </h3>
                <div className="space-y-4">
                  {visible.map((spec) => (
                    <MetricChart
                      key={spec.metricKey}
                      spec={spec}
                      pool={pool}
                      deploys={deploys}
                      onPointClick={openAudit}
                      comparePool={compare ? guestPool : undefined}
                      homeKey={homeKey}
                      compareKey={compareKey}
                      pageNameById={pageNameById}
                    />
                  ))}
                </div>
              </section>
            );
          })}
    </div>
  );
}

/** Loading placeholder for a single MetricChart. Matches the populated
 *  shape (label + value header row, then a chart-sized bordered frame)
 *  so the page doesn't restructure when audits land. */
function MetricChartPlaceholder({ label }: { label: string }): ReactElement {
  return (
    <div aria-hidden="true" className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 px-1">
        <span className="text-xs font-medium text-[var(--color-ink-soft)]">{label}</span>
        <span className="text-xs font-semibold text-[var(--color-mute)] tabular-nums">—</span>
      </div>
      <div className="h-[160px] rounded-xl border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)]/30" />
    </div>
  );
}
